/**
 * Beyond Cheeky product page helpers
 *
 * 1. Size chart link: shows a "Size chart" link under the size picker and
 *    opens the matching chart image in a popup. Charts and their tags are
 *    managed in the theme editor in the "OnHOW Size Chart" section on the
 *    product template (it outputs a JSON config this script reads).
 *
 * 2. Require a size: no size is preselected when a shopper lands on a
 *    product page, and any "Add to cart" button (including sticky bars from
 *    apps) is blocked with a "Please choose a size" prompt until a size is
 *    picked. If the page is opened with ?variant= in the URL (e.g. from an
 *    ad or Google Shopping), that variant's size is kept as chosen.
 */
(function () {
  'use strict';

  /* ---------------- Require a size ---------------- */

  var SIZE_MSG = 'Please choose a size';
  var sizeChosen = /[?&]variant=/.test(window.location.search);

  function sizeFieldsets() {
    return Array.prototype.filter.call(
      document.querySelectorAll('variant-selects fieldset'),
      function (fs) {
        var legend = fs.querySelector('legend');
        return legend && /size/i.test(legend.textContent);
      }
    );
  }

  function clearSizeSelection() {
    if (sizeChosen) return;
    sizeFieldsets().forEach(function (fs) {
      fs.querySelectorAll('input[type="radio"]').forEach(function (input) {
        input.checked = false;
        input.removeAttribute('checked');
      });
    });
  }

  function needsSize() {
    if (sizeChosen) return false;
    var fieldsets = sizeFieldsets();
    if (!fieldsets.length) return false;
    return !fieldsets.some(function (fs) {
      return fs.querySelector('input[type="radio"]:checked');
    });
  }

  function showSizeError() {
    var fs = sizeFieldsets()[0];
    if (!fs) return;
    fs.classList.add('bc-size-required--error');
    var msg = fs.querySelector('.bc-size-required__msg');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'bc-size-required__msg';
      msg.setAttribute('role', 'alert');
      msg.textContent = SIZE_MSG;
      fs.appendChild(msg);
    }
    fs.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function clearSizeError(fs) {
    fs.classList.remove('bc-size-required--error');
    var msg = fs.querySelector('.bc-size-required__msg');
    if (msg) msg.remove();
  }

  function guard(evt) {
    if (!needsSize()) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
    showSizeError();
  }

  // A size was picked by the shopper.
  document.addEventListener(
    'change',
    function (evt) {
      var fs = evt.target && evt.target.closest ? evt.target.closest('fieldset') : null;
      if (fs && sizeFieldsets().indexOf(fs) !== -1) {
        sizeChosen = true;
        clearSizeError(fs);
      }
    },
    true
  );

  // Block add-to-cart form submits until a size is chosen.
  window.addEventListener(
    'submit',
    function (evt) {
      var form = evt.target;
      if (form && form.action && /\/cart\/add/.test(form.action)) guard(evt);
    },
    true
  );

  // Block any "Add to cart" button (main button, sticky bars, app buttons).
  window.addEventListener(
    'click',
    function (evt) {
      var el = evt.target && evt.target.closest
        ? evt.target.closest('button, a, [role="button"], input[type="submit"]')
        : null;
      if (!el || el.closest('variant-selects') || el.closest('.bc-size-modal')) return;
      var text = (el.textContent || el.value || '').replace(/\s+/g, ' ').trim();
      if (el.name === 'add' || /add to (cart|bag)/i.test(text)) guard(evt);
    },
    true
  );

  // Dawn re-renders the size buttons when the colour changes; keep them
  // unselected until the shopper actually picks a size.
  function watchRerenders() {
    var root = document.querySelector('product-info') || document.body;
    var pending = false;
    new MutationObserver(function () {
      if (sizeChosen || pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        clearSizeSelection();
      });
    }).observe(root, { childList: true, subtree: true });
  }

  /* ---------------- Size chart link ---------------- */

  function initSizeChart() {
    var wrap = document.querySelector('.bc-size-help');
    var modal = document.querySelector('.bc-size-modal');
    var configEl = document.querySelector('[id^="onhow-sizechart-config-"]');
    if (!wrap || !modal || !configEl) return;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    var productTags = (wrap.getAttribute('data-product-tags') || '')
      .split(',')
      .map(function (t) { return t.trim(); })
      .filter(Boolean);

    var chart = null;
    (config.charts || []).some(function (c) {
      var chartTags = (c.tags || '').toLowerCase().split(',').map(function (t) { return t.trim(); });
      var match = productTags.some(function (t) { return chartTags.indexOf(t) !== -1; });
      if (match && c.image) chart = c;
      return !!chart;
    });
    if (!chart && config.fallback && config.fallback.image) chart = config.fallback;
    if (!chart) return;

    var img = modal.querySelector('.bc-size-modal__img');
    var title = modal.querySelector('.bc-size-modal__title');
    var note = modal.querySelector('.bc-size-modal__note');
    if (chart.name) title.textContent = chart.name.replace(/\.$/, '');
    var description = chart.description || (config.global_settings && config.global_settings.default_description);
    if (description) note.innerHTML = description;

    document.body.appendChild(modal);
    wrap.hidden = false;

    var lastFocus = null;
    function open() {
      if (!img.getAttribute('src')) img.setAttribute('src', chart.image);
      lastFocus = document.activeElement;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      modal.querySelector('.bc-size-modal__close').focus();
    }
    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    }

    wrap.querySelector('.bc-size-help__link').addEventListener('click', open);
    modal.querySelector('.bc-size-modal__close').addEventListener('click', close);
    modal.addEventListener('click', function (evt) {
      if (evt.target === modal) close();
    });
    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && !modal.hidden) close();
    });
  }

  function init() {
    clearSizeSelection();
    watchRerenders();
    initSizeChart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
